import {
    FETCH_LANGUAGE,
    FETCH_LANGUAGE_SUCCESS,
    FETCH_LANGUAGE_FAILED,
    FETCH_LANGUAGE_PACK_SUCCESS,
    EDIT_LANGUAGE
} from "../store/types";
import { firebase } from '../config/configureFirebase';
import { get, set, push, remove, update } from "firebase/database";
import { getLangKey } from "../other/getLangKey";

// ─────────────────────────────────────────────────────────────────────────────
// Language packs: load the default + the user's language only.
//
// Every cold start / portal load used to download the WHOLE /languages node
// (all five packs, ~650 KB). Now:
//   1. read /languages_meta (~1 KB): every language's id, locale, name, date
//      locale, default flag and a content hash (server-maintained,
//      functions/common/languagesMeta.js);
//   2. load the default pack and the user's pack (opts.locale) — and reuse a
//      cached copy (opts.cached = the langlist saved by the app) whose hash
//      still matches, so an unchanged pack costs nothing;
//   3. every other language stays listed (pickers keep working) without its
//      keyValuePairs; loadLanguagePack() fetches one on demand when the user
//      switches to it.
// Whenever the index is missing, unreadable or has no default language (not
// deployed yet, rules not deployed, …) this falls back to the full download —
// exactly the old behaviour. The admin language editor asks for the full set
// (opts.full) because it saves whole language nodes.
// ─────────────────────────────────────────────────────────────────────────────

const isObj = (v) => Boolean(v) && typeof v === 'object' && !Array.isArray(v);
const hasPack = (kvp) => isObj(kvp) && Object.keys(kvp).length > 0;
const listOf = (v) => (Array.isArray(v) ? v : (isObj(v) ? Object.values(v) : []));

// The old path: every pack in one read.
const fetchAllLanguages = async (dispatch, languagesRef) => {
    const snapshot = await get(languagesRef);
    if (snapshot.val()) {
        const data = snapshot.val();
        let defLang = null;
        const arr = Object.keys(data).map(i => {
            data[i].id = i;
            if(data[i].default){
                defLang = data[i].keyValuePairs;
            }
            return data[i]
        });
        dispatch({
            type: FETCH_LANGUAGE_SUCCESS,
            payload: {
                defaultLanguage: defLang,
                langlist: arr,
                full: true
            }
        });
    } else {
        dispatch({
            type: FETCH_LANGUAGE_FAILED,
            payload: "No Languages available."
        });
    }
};

// opts = { locale: the user's langLocale (or locales: several — e.g. the
//          phone's saved choice and the signed-in profile's), cached: the
//          previously saved langlist, full: true to load every pack }
// Always loaded: the default pack and English (the apps' fallback locale).
export const fetchLanguages = (opts) => async (dispatch) => {

    const {
        languagesRef,
        languagesMetaRef,
        languagePackRef
    } = firebase;
    const o = opts || {};

    dispatch({
        type: FETCH_LANGUAGE,
        payload: null
    });
    try {
        if (o.full === true) {
            await fetchAllLanguages(dispatch, languagesRef);
            return;
        }
        let langs = null;
        try {
            const metaSnap = await get(languagesMetaRef);
            const meta = metaSnap.val();
            langs = meta && isObj(meta.langs) ? meta.langs : null;
        } catch (e) {
            langs = null; // index unreadable (rules not deployed, offline …) → full download below
        }
        const ids = langs ? Object.keys(langs).filter((id) => isObj(langs[id]) && typeof langs[id].langLocale === 'string' && langs[id].langLocale) : [];
        const defId = ids.find((id) => langs[id].default === true);
        if (!defId) {
            await fetchAllLanguages(dispatch, languagesRef);
            return;
        }
        const wantLocales = ['en'].concat(listOf(o.locales), o.locale ? [o.locale] : []);
        const want = [defId];
        wantLocales.forEach((loc) => {
            const id = loc ? ids.find((x) => langs[x].langLocale === loc) : null;
            if (id && want.indexOf(id) < 0) want.push(id);
        });
        const cached = {};
        listOf(o.cached).forEach((c) => {
            if (isObj(c) && c.id && hasPack(c.keyValuePairs)) cached[c.id] = c;
        });
        const packs = {};
        const staleIds = {};   // packs kept from the cache although the index has moved on
        await Promise.all(want.map(async (id) => {
            const c = cached[id];
            if (c && c.hash && c.hash === langs[id].hash) {
                packs[id] = c.keyValuePairs;
                return;
            }
            try {
                const snap = await get(languagePackRef(id));
                const kvp = snap.val();
                if (hasPack(kvp)) packs[id] = kvp;
            } catch (e) {
                // Handled just below.
            }
            if (!packs[id] && c) {
                // The refresh failed: keep the copy on screen rather than drop
                // to English; no hash, so the next start downloads it again.
                packs[id] = c.keyValuePairs;
                staleIds[id] = true;
            }
        }));
        if (!packs[defId]) {
            await fetchAllLanguages(dispatch, languagesRef);
            return;
        }
        const langlist = ids.map((id) => {
            const m = langs[id];
            const entry = {
                id: id,
                langLocale: m.langLocale,
                langName: m.langName || m.langLocale,
                dateLocale: m.dateLocale || m.langLocale,
                default: m.default === true,
                hash: staleIds[id] ? null : (m.hash || null)
            };
            if (packs[id]) entry.keyValuePairs = packs[id];
            return entry;
        });
        dispatch({
            type: FETCH_LANGUAGE_SUCCESS,
            payload: {
                defaultLanguage: packs[defId],
                langlist: langlist,
                full: false
            }
        });
    } catch (error) {
        dispatch({
            type: FETCH_LANGUAGE_FAILED,
            payload: error.message
        });
    }
};

// Make sure a language's pack is in the store before switching to it.
// Resolves { success, keyValuePairs } — success false (with error) when the
// language is unknown or its pack cannot be read right now. The Firebase SDK
// never rejects a read while offline (it waits for the connection), so the
// caller's wait is capped at opts.timeoutMs (default 8 s) and answers
// { success: false, error: 'offline' }; the download itself carries on and
// still merges the pack when the connection returns. Repeated calls for the
// same pack share one download.
const packInFlight = {};
const packRefreshed = {};   // held packs of unknown age already refreshed this session
const PACK_WAIT_MS = 8000;
// One download per language at a time; it merges the pack into the store.
const startPackDownload = (id, dispatch) => {
    if (!packInFlight[id]) {
        packInFlight[id] = get(firebase.languagePackRef(id)).then((snap) => {
            const kvp = snap.val();
            if (!hasPack(kvp)) return { success: false, error: 'empty language pack' };
            dispatch({
                type: FETCH_LANGUAGE_PACK_SUCCESS,
                payload: { id: id, keyValuePairs: kvp }
            });
            return { success: true, keyValuePairs: kvp };
        }, (error) => ({ success: false, error: (error && error.message) || 'read failed' }))
            .then((r) => { delete packInFlight[id]; return r; });
    }
    return packInFlight[id];
};
export const loadLanguagePack = (langLocale, opts) => async (dispatch, getState) => {
    const state = typeof getState === 'function' ? getState() : null;
    const list = listOf(state && state.languagedata ? state.languagedata.langlist : null);
    const entry = list.find((l) => l && l.langLocale === langLocale);
    if (!entry) return { success: false, error: 'unknown language' };
    if (hasPack(entry.keyValuePairs)) {
        // A pack already held switches at once. One without a hash is a copy
        // kept from an older cache that may be out of date (the index moved
        // on, or the copy predates the index): refresh it once per session in
        // the background, so the screens pick up the current text on their
        // next render instead of only after the next cold start. Not after a
        // full load: those packs were all just read from the server.
        const full = !!(state && state.languagedata && state.languagedata.full === true);
        if (!full && !entry.hash && entry.id && !packRefreshed[entry.id]) {
            const id = entry.id;
            packRefreshed[id] = true;
            startPackDownload(id, dispatch).then((r) => { if (!r || !r.success) packRefreshed[id] = false; });
        }
        return { success: true, keyValuePairs: entry.keyValuePairs };
    }
    if (!entry.id) return { success: false, error: 'unknown language' };
    const download = startPackDownload(entry.id, dispatch);
    const waitMs = opts && typeof opts.timeoutMs === 'number' ? opts.timeoutMs : PACK_WAIT_MS;
    if (!(waitMs > 0)) return download;
    let timer = null;
    const timeout = new Promise((resolve) => {
        timer = setTimeout(() => resolve({ success: false, error: 'offline' }), waitMs);
    });
    const r = await Promise.race([download, timeout]);
    clearTimeout(timer);
    return r;
};

// Client-side fields that must never be written into a language node.
const forStorage = (lang) => {
    const out = {};
    Object.keys(lang || {}).forEach((k) => {
        if (k !== 'tableData' && k !== 'hash') out[k] = lang[k];
    });
    return out;
};

export const editLanguage = (lang, method) => (dispatch) => {
    const {
        languagesRef,
        languagesEditRef
    } = firebase;
    // "Add" and "Update" write the WHOLE language node. A language whose pack
    // is not loaded on this page (partial loading) would be written without
    // its keyValuePairs and lose every translation — refuse instead.
    if (method !== 'Delete' && !hasPack(lang && lang.keyValuePairs)) {
        console.warn('[editLanguage] refused: ' + ((lang && (lang.langLocale || lang.id)) || 'language') + ' has no loaded keyValuePairs; saving would erase its pack.');
        return { success: false, error: 'language pack not loaded' };
    }
    dispatch({
        type: EDIT_LANGUAGE,
        payload: { lang, method }
    });
    if (method === 'Add') {
        push(languagesRef, forStorage(lang));
    } else if (method === 'Delete') {
        remove(languagesEditRef(lang.id));
    } else {
        set(languagesEditRef(lang.id), forStorage(lang));
    }
    return { success: true };
}


export const convertLanguage = async (word, userLangLocale)=>{
    const {
        languagesRef,
        langEditRef,
        config,
    } = firebase;

    let langKey = getLangKey(word);

    const snapshot = await get(languagesRef);
    if (snapshot.val()) {
        const data = snapshot.val();
        const langLocaleArr = Object.keys(data).map(i => {
            data[i].id = i;
            return {langLocale : data[i].langLocale, id: data[i].id}
        });

        let defLangLocale = "";

        if(userLangLocale){
            defLangLocale = userLangLocale;
        }else{
            const defLocale = Object.keys(data).filter(i => {
                data[i].id = i;
                return data[i].default === true;
            })[0];

            defLangLocale = data[defLocale].langLocale;
        }

        for (let j = 0; j < langLocaleArr.length; j++){
            try{
                if(langLocaleArr[j].langLocale === defLangLocale){
                    update(langEditRef(langLocaleArr[j].id),{[langKey]:word})
                }else{
                    const response = await fetch(`https://us-central1-${config.projectId}.cloudfunctions.net/gettranslation?str=${word}&from=${defLangLocale}&to=${langLocaleArr[j].langLocale}`, {
                        method: 'GET',
                        headers: {
                          'Content-Type': 'application/json'
                        }
                      })
                      const json = await response.json();
                      if(json){
                        update(langEditRef(langLocaleArr[j].id),{[langKey]:json.text})
                      }
                }
              }
              catch(err) {
                console.log("error occurred in language add", err)
              };
        }
    }

}